import React from 'react';
import _ from 'lodash';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import Editor from 'ckeditor5-build-full';
import { Navigate, Input, Message } from '../components';
import {__, formatCurrency, getCurrency, getCurrencyPrice, request, toNumber} from '../functions';
import '../assets/styles/category.css';
import {ButtonBase, Tab, Tabs} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Category komponenta.
 */
class Category extends Navigate {
    static defaultProps = {
        id: 0,
        user: {},
    };

    state = {
        tab: 'translations',
        subtab: '',
        data: {},
        settings: {},
        loading: false,
        languages: [],
        loadingsAi: {},
        loadingsSave: {},
        loadingsRemove: {},
        ai_commands: [],
    };

    ai_commands_interval = null;
    ai_commands_interval_counter = 0;

    componentDidMount() {
        this.fetch();
    }

    componentWillUnmount() {
        this.clearAiCommandsInterval();
    }

    fetch(force_subtab = '') {
        const { id, onClose, setTitle, showSnackbar } = this.props;
        const { tab } = this.state;

        request(`/categories/view/${id}`).then(response => {
            const { status, data } = response.data;

            if (status === 'error') {
                showSnackbar('error', __('Kategória neexistuje'));
                onClose();
                return;
            }

            let languages = [];
            let subtab = '';

            if (!_.isEmpty(data.category.translations)) {
                // Nastavime title
                setTitle(_.truncate(data.category.translations[0].name, { length: this.isMobile() ? 32 : 64 }));

                _.each(data.category.translations, translation => {
                    if (!_.includes(languages, translation.language)) {
                        languages = [ ...languages, translation.language ];
                    }
                });

                if (languages.length > 1) {
                    subtab = force_subtab !== '' ? force_subtab : languages[0];
                }
            }

            this.setState({ data, languages, subtab, ...this.getAiCommands(data.ai_commands, tab, subtab) });

            if (this.hasProcessingAiCommand(data.ai_commands)) {
                this.setAiCommandsInterval();
            }
        });
    }

    onChangeSetting(name, value) {
        const { settings } = this.state;

        this.setState({ settings: { ...settings, [name]: value } });
    }

    onChangeTab(tab) {
        let { subtab, languages, data } = this.state;

        switch (tab) {
            case 'translations':
                subtab = languages.length > 1 ? languages[0] : '';
                break;
        }

        this.setState({ tab, subtab, ...this.getAiCommands(data.ai_commands, tab, subtab) });
    }

    onChangeSubTab(subtab) {
        const { tab, data } = this.state;

        this.setState({ subtab, ...this.getAiCommands(data.ai_commands, tab, subtab) });
    }

    renderValue(value) {
        return !_.isEmpty(value) ? value : '-';
    }

    showHtmlEditor(value) {
        return value.toString().indexOf('</p>') !== -1 || value.toString().indexOf('</ul>') !== -1;
    }

    async generateAi(field, aiFunctionSlug) {
        const { id } = this.props;
        const { loadingsAi, subtab } = this.state;

        this.setState({ loadingsAi: { ...loadingsAi, [field]: field }, loadingsSave: {}, loadingsRemove: {} });

        let variable_inputs = [];
        let variables = [];
        let force_input = '';

        if (aiFunctionSlug === this.getAiTranslateFunctionSlug()) {
            variable_inputs = [this.getAiTranslateFieldByField(field)];
            variables = { language: subtab };
        } else {
            switch (aiFunctionSlug) {
                case 'GenerateCategoryShortDescription':
                    variable_inputs = [
                        'category_name',
                        'category_description',
                    ];
                    break;

                case 'GenerateCategoryDescription':
                    variable_inputs = [
                        'category_name',
                        'category_short_description',
                    ];
                    break;

                case 'GenerateCategorySeoTitle':
                case 'GenerateCategorySeoDescription':
                    variable_inputs = [
                        'category_name',
                        'category_short_description',
                        'category_description',
                    ];
                    break;
            }
        }

        let created_ai_command = false;

        await request(`/categories/multiAi/${aiFunctionSlug}/${id}`, {
            variable_inputs,
            variables,
            force_input,
            save_reference: true,
        }, 'POST').then(response => {
            const { status } = response.data;

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa vytvoriť AI úlohu'));

                this.setState({ loadingsAi: {} });
                return;
            }

            created_ai_command = true;
        });

        if (created_ai_command) {
            this.setAiCommandsInterval();
        }
    }

    fetchAiCommands(force = false) {
        const { tab, subtab } = this.state;
        const { id } = this.props;

        this.ai_commands_interval_counter++;

        if (!force && (this.ai_commands_interval_counter > 30 || tab !== 'translations')) {
            this.clearAiCommandsInterval();
            return;
        }

        request(`/categories/getAiCommands/${id}`).then(response => {
            const { status, data } = response.data;

            if (status === 'success') {
                let new_state = this.getAiCommands(data.ai_commands, tab, subtab);
                new_state = { ...new_state, data: { ...this.state.data, ai_commands: data.ai_commands } };

                if (!this.hasProcessingAiCommand(data.ai_commands)) {
                    new_state = { ...new_state, loadingsAi: {} };

                    this.clearAiCommandsInterval();
                }

                this.setState(new_state);
            }
        });
    }

    setAiCommandsInterval() {
        this.clearAiCommandsInterval();

        this.ai_commands_interval = setInterval(() => {
            this.fetchAiCommands();
        }, 20000);
    }

    clearAiCommandsInterval() {
        if (this.ai_commands_interval !== null) {
            clearInterval(this.ai_commands_interval);

            this.ai_commands_interval = null;
            this.ai_commands_interval_counter = 0;
        }
    }

    getSaveFieldsWithoutLanguage() {
        const { data } = this.state;

        return _.reduce(data.save_fields, (result, item) => {
            const language_field = /^([a-z]{2})\.(.*)$/.exec(item);

            if (
                language_field !== null
                && language_field.length === 3
            ) {
                // Remove language prefix
                return [ ...result, language_field[2] ];
            }

            return [ ...result, item ];
        }, []);
    }

    getMetaFieldName(field) {
        if (field === 'name') {
            return '';
        }

        const { data, subtab } = this.state;
        const saveFieldsWithoutLanguage = this.getSaveFieldsWithoutLanguage();
        const allowMeta = _.includes(saveFieldsWithoutLanguage, 'meta.{meta_key}');

        if (!allowMeta || _.includes(saveFieldsWithoutLanguage, this.convertSaveField(field))) {
            return '';
        }

        const setting_meta_key = `meta_data_category_${field}`;
        let meta_suffix = '';

        if (this.isTranslate(subtab)) {
            meta_suffix = `_${subtab}`;
        }

        return _.has(data.eshop_settings, setting_meta_key) ? `meta.${data.eshop_settings[setting_meta_key]}${meta_suffix}` : '';
    }

    convertSaveField(field) {
        switch (field) {
            case 'name':
                return 'title';

            case 'short_description':
                return 'short_description';

            case 'description':
                return 'long_description';

            case 'seo_title':
                return 'seo_title';

            case 'seo_description':
                return 'seo_description';
        }

        return '';
    }

    getSaveField(field) {
        const { user } = this.props;
        const { data, subtab } = this.state;

        let save_field = this.convertSaveField(field);
        const meta_field = this.getMetaFieldName(field);

        if (meta_field !== '') {
            return meta_field;
        }

        if (save_field === '') {
            return '';
        }

        const language_save_fields = _.reduce(data.save_fields, (result, item) => {
            const language_field = /^([a-z]{2})\.(.*)$/.exec(item);

            if (
                language_field !== null
                && language_field.length === 3
                && language_field[2] === save_field
            ) {
                return { ...result, [language_field[1]]: item };
            }

            return result;
        }, {});

        if (!_.isEmpty(language_save_fields)) {
            let language = subtab;

            if (language === '') {
                language = user.settings.market;
            }

            if (_.has(language_save_fields, language)) {
                return language_save_fields[language];
            }
        }

        if (subtab === '') {
            // Without language
            return save_field;
        }

        return '';
    }

    async save(field, ai_command) {
        const { loadingsSave, subtab } = this.state;

        this.setState({ loadingsSave: { ...loadingsSave, [field]: field } });

        const save_field = this.getSaveField(field);

        if (save_field === '') {
            this.showSnackbar('error', __('V eshope neexistuje údaj pre uloženie'));
            return;
        }

        const ai_command_id = ai_command._matchingData.AiCommands.id;
        const ids = ai_command._matchingData.AiCommandItems.id;
        let force_output = this.getTranslationFieldValue(this.getSelectedTranslation(subtab), field).value;

        if (this.isDemo()) {
            force_output = '';
        }

        await request(`/ai-command-items/multiSave/${ai_command_id}/${save_field}/${ids}`, { force_output }, 'POST').then(response => {
            const { data, status } = response.data;

            if (status === 'error' || !_.isEmpty(data.errors)) {
                this.showSnackbar('error', __('Nepodarilo sa uložiť v eshope'));
                return;
            }

            this.showSnackbar('success', __('Uložené'));
        });

        this.setState({ data: {}, settings: {}, loadingsAi: {}, loadingsSave: {} });
        this.fetch(subtab);
    }

    async remove(field, ai_command_id) {
        const { loadingsRemove, subtab } = this.state;

        this.setState({ loadingsRemove: { ...loadingsRemove, [field]: field } });

        await request(`/ai-commands/delete/${ai_command_id}`).then(response => {});

        this.setState({ data: {}, settings: {}, loadingsRemove: {} });
        this.fetch(subtab);
    }

    hasProcessingAiCommand(ai_commands) {
        let result = false;

        _.each(ai_commands, ai_command => {
            if (_.includes(['waiting', 'processing'], ai_command._matchingData.AiCommandItems.status)) {
                result = true;
            }
        });

        return result;
    }

    getAiCommands(ai_commands, tab, subtab) {
        const isTranslate = this.isTranslate(subtab);
        let settings = {};

        _.each(['name', 'short_description', 'description', 'seo_title', 'seo_description'], field => {
            const aiFunctionSlug = isTranslate ? this.getAiTranslateFunctionSlug() : this.getAiFunctionByField(field);
            const aiCommand = this.getAiCommand(ai_commands, aiFunctionSlug, field, subtab);

            if (aiCommand !== null && aiCommand._matchingData.AiCommandItems.status === 'processed') {
                settings = { ...settings, [field]: aiCommand._matchingData.AiCommandItems.output };
            }
        });

        return {
            ai_commands,
            settings,
        };
    }

    getAiCommand(ai_commands, aiFunctionSlug, field, language) {
        let command = null;
        let has_last_saved = false;

        _.each(ai_commands, ai_command => {
            if (
                ai_command.slug === aiFunctionSlug
                && command === null
                && !has_last_saved
            ) {
                // Last not saved command
                const is_saved = toNumber(ai_command._matchingData.AiCommandItems.saved) === 1;

                if (aiFunctionSlug === this.getAiTranslateFunctionSlug()) {
                    // Translate compare language and translated field
                    const translate_field = this.getAiTranslateFieldByField(field);

                    if (
                        _.isObject(ai_command._matchingData.AiCommands.settings)
                        && _.has(ai_command._matchingData.AiCommands.settings, 'language')
                        && _.has(ai_command._matchingData.AiCommands.settings, 'translate_field')
                        && ai_command._matchingData.AiCommands.settings.language === language
                        && ai_command._matchingData.AiCommands.settings.translate_field === translate_field
                    ) {
                        if (is_saved) {
                            has_last_saved = true;
                        } else {
                            command = ai_command;
                        }
                    }
                } else {
                    if (is_saved) {
                        has_last_saved = true;
                    } else {
                        command = ai_command;
                    }
                }
            }
        });

        return command;
    }

    getAiTranslateFieldByField(field) {
        switch (field) {
            case 'name':
                return 'category_name';

            case 'short_description':
                return 'category_short_description';

            case 'description':
                return 'category_description';

            case 'seo_title':
                return 'category_seo_title';

            case 'seo_description':
                return 'category_seo_description';
        }

        return '';
    }

    getAiFunctionByField(field) {
        switch (field) {
            case 'short_description':
                return 'GenerateCategoryShortDescription';

            case 'description':
                return 'GenerateCategoryDescription';

            case 'seo_title':
                return 'GenerateCategorySeoTitle';

            case 'seo_description':
                return 'GenerateCategorySeoDescription';
        }

        return '';
    }

    getAiTranslateFunctionSlug() {
        return 'TranslatesTextIntoTheSelectedLanguage';
    }

    isTranslate(subtab) {
        const { user } = this.props;

        return subtab !== '' && subtab !== user.settings.market;
    }

    getTranslationFieldValue(selectedTranslation, field, useSettings = true) {
        const { settings, data } = this.state;

        const meta_field = this.getMetaFieldName(field).replace('meta.', '');
        const saveFieldsWithoutLanguage = this.getSaveFieldsWithoutLanguage();
        const inSelectedTranslation = _.includes(saveFieldsWithoutLanguage, this.convertSaveField(field));

        if (_.has(settings, field) && useSettings) {
            return {
                value: settings[field],
                meta: !inSelectedTranslation ? meta_field : '',
            };
        }

        if (inSelectedTranslation) {
            return {
                value: selectedTranslation[field],
                meta: meta_field,
            };
        }

        if (field === 'name') {
            return {
                value: '',
                meta: '',
            };
        }

        if (!_.isEmpty(data.category.meta_data) && _.has(data.category.meta_data, meta_field)) {
            return {
                value: data.category.meta_data[meta_field],
                meta: meta_field,
            };
        }

        return {
            value: '',
            meta: meta_field,
        };
    }

    getSelectedTranslation(subtab) {
        const { data } = this.state;

        let selectedTranslation = null;

        _.map(data.category.translations, translation => {
            if (subtab === '') {
                selectedTranslation = translation;
            }

            if (translation.language === subtab) {
                selectedTranslation = translation;
            }
        });

        return selectedTranslation;
    }

    renderAiButtons(field, nameValue = '') {
        const { loadingsAi, loadingsSave, loadingsRemove, subtab, ai_commands } = this.state;

        const saveFieldsWithoutLanguage = this.getSaveFieldsWithoutLanguage();
        const allowMeta = _.includes(saveFieldsWithoutLanguage, 'meta.{meta_key}');
        const isTranslate = this.isTranslate(subtab);

        if (field === 'name' && !isTranslate) {
            return null;
        } else if (field === 'short_description' && !_.includes(saveFieldsWithoutLanguage, 'short_description') && !allowMeta) {
            return null;
        } else if (field === 'description' && !_.includes(saveFieldsWithoutLanguage, 'long_description') && !allowMeta) {
            return null;
        } else if (field === 'seo_title' && !_.includes(saveFieldsWithoutLanguage, 'seo_title') && !allowMeta) {
            return null;
        } else if (field === 'seo_description' && !_.includes(saveFieldsWithoutLanguage, 'seo_description') && !allowMeta) {
            return null;
        }

        let aiFunctionSlug = this.getAiTranslateFunctionSlug();

        if (!isTranslate) {
            aiFunctionSlug = this.getAiFunctionByField(field);
        }

        const ai_command = this.getAiCommand(ai_commands, aiFunctionSlug, field, subtab);

        let isAiLoading = _.has(loadingsAi, field);
        let isSaveLoading = _.has(loadingsSave, field);
        let isRemoveLoading = _.has(loadingsRemove, field);
        let showSave = false;
        let showError = false;
        let errorMessage = '';
        let price = null;
        let ai_command_id = 0;

        if (ai_command !== null) {
            switch (ai_command._matchingData.AiCommandItems.status) {
                case 'waiting':
                case 'processing':
                    isAiLoading = true;
                    break;

                case 'processed':
                    showSave = true;
                    ai_command_id = ai_command._matchingData.AiCommands.id;
                    price = `${getCurrencyPrice(ai_command._matchingData.AiCommands.price).replace('.', ',')} ${formatCurrency(getCurrency())}`;

                    if (!_.isEmpty(ai_command._matchingData.AiCommandItems.error_save)) {
                        showError = true;
                        errorMessage = ai_command._matchingData.AiCommandItems.error_save;
                    }
                    break;

                case 'error':
                    showError = true;
                    errorMessage = ai_command._matchingData.AiCommandItems.error;

                    switch (errorMessage) {
                        case 'Empty credit':
                            errorMessage = __('Nemáte dostatočný kredit');
                            break;
                    }
                    break;
            }
        }

        const disabledGenerateAi = !_.isEmpty(loadingsSave);
        let disabledSave = (!isSaveLoading && !_.isEmpty(loadingsSave)) || !_.isEmpty(loadingsAi) || !_.isEmpty(loadingsRemove);
        let emptyName = false;

        if (field !== 'name' && _.isEmpty(nameValue)) {
            disabledSave = true;
            emptyName = true;
        }

        return (
            <div className="category-content__panel__tab-content__buttons">
                <ButtonBase
                    onClick={!disabledGenerateAi ? () => this.generateAi(field, aiFunctionSlug) : () => {}}
                    className={`category-content__panel__tab-content__buttons__button-ai ${disabledGenerateAi ? 'category-content__panel__tab-content__buttons__button-disabled' : ''}`}
                >
                    {isAiLoading ? this.renderLoading(16) : <SmartToyIcon />}
                    <span>{isAiLoading
                        ? __('Spracováva sa')
                        : (isTranslate ? __('Preložiť cez AI') : __('Vygenerovať cez AI'))}
                    </span>
                </ButtonBase>
                {showSave ? <ButtonBase
                    onClick={!disabledSave ? () => this.save(field, ai_command) : () => {}}
                    className={`category-content__panel__tab-content__buttons__button-save ${disabledSave ? 'category-content__panel__tab-content__buttons__button-disabled' : ''}`}
                >
                    {isSaveLoading ? this.renderLoading(16) : <SaveIcon />}
                    <span>{isSaveLoading ? null : (emptyName ? __('Názov nie je vyplnený a uložený') : __('Uložiť do eshopu'))}</span>
                </ButtonBase> : null}
                {showSave && toNumber(ai_command_id) > 0 && !isSaveLoading && !isAiLoading ? <ButtonBase
                    onClick={!isRemoveLoading ? () => this.remove(field, ai_command_id) : () => {}}
                    className="category-content__panel__tab-content__buttons__button-remove"
                >
                    {isRemoveLoading ? this.renderLoading(16) : <DeleteIcon />}
                    <span>{isRemoveLoading ? null : __('Zahodiť')}</span>
                </ButtonBase> : null}
                {showError ? <div className="category-content__panel__tab-content__buttons__error">{errorMessage}</div> : null}
                {showSave && !showError && price !== null ? <div className="category-content__panel__tab-content__buttons__price">{__('Cena')}: {price}</div> : null}
            </div>
        );
    }

    renderTab() {
        const { tab, subtab, data } = this.state;

        switch (tab) {
            case 'translations':
                const selectedTranslation = this.getSelectedTranslation(subtab);

                if (selectedTranslation === null) {
                    return null;
                }

                const allowMeta = _.includes(this.getSaveFieldsWithoutLanguage(), 'meta.{meta_key}');
                const name = this.getTranslationFieldValue(selectedTranslation, 'name');
                const nameWithoutSettings = this.getTranslationFieldValue(selectedTranslation, 'name', false);
                const short_description = this.getTranslationFieldValue(selectedTranslation, 'short_description');
                const description = this.getTranslationFieldValue(selectedTranslation, 'description');
                const seo_title = this.getTranslationFieldValue(selectedTranslation, 'seo_title');
                const seo_description = this.getTranslationFieldValue(selectedTranslation, 'seo_description');

                const nameButtons = this.renderAiButtons('name');
                const shortDescriptionButtons = this.renderAiButtons('short_description', nameWithoutSettings.value);
                const descriptionButtons = this.renderAiButtons('description', nameWithoutSettings.value);
                const seoTitleButtons = this.renderAiButtons('seo_title', nameWithoutSettings.value);
                const seoDescriptionButtons = this.renderAiButtons('seo_description', nameWithoutSettings.value);

                return (
                    <>
                        <Message type="warning">{__('Dáta ku kategóriam cez AI viete generovať aj hromadne v zozname kategórii.')}</Message>
                        <Input
                            label={__('Názov')}
                            value={name.value}
                            onChange={value => this.onChangeSetting('name', value)}
                        />
                        {nameButtons}
                        {shortDescriptionButtons !== null && this.showHtmlEditor(short_description.value) ? <div className="input__label editor-label">{`${__('Krátky popis')}${allowMeta && short_description.meta !== '' ? ` (meta: ${short_description.meta})` : ''}`}</div> : null}
                        {shortDescriptionButtons !== null ? (this.showHtmlEditor(short_description.value) ? <CKEditor
                            editor={Editor}
                            data={short_description.value}
                            onChange={(event, editor) => this.onChangeSetting('short_description', editor.getData())}
                        /> : <Input
                            label={`${__('Krátky popis')}${allowMeta && short_description.meta !== '' ? ` (meta: ${short_description.meta})` : ''}`}
                            value={short_description.value}
                            onChange={value => this.onChangeSetting('short_description', value)}
                            multiline
                        />) : null}
                        {shortDescriptionButtons}
                        {descriptionButtons !== null && this.showHtmlEditor(description.value) ? <div className="input__label editor-label">{`${__('Dlhý popis')}${allowMeta && description.meta !== '' ? ` (meta: ${description.meta})` : ''}`}</div> : null}
                        {descriptionButtons !== null ? (this.showHtmlEditor(description.value) ? <CKEditor
                            editor={Editor}
                            data={description.value}
                            onChange={(event, editor) => this.onChangeSetting('description', editor.getData())}
                        /> : <Input
                            label={`${__('Dlhý popis')}${allowMeta && description.meta !== '' ? ` (meta: ${description.meta})` : ''}`}
                            value={description.value}
                            onChange={value => this.onChangeSetting('description', value)}
                            multiline
                        />) : null}
                        {descriptionButtons}
                        {seoTitleButtons !== null ? <Input
                            label={`${__('SEO názov')}${allowMeta && seo_title.meta !== '' ? ` (meta: ${seo_title.meta})` : ''}`}
                            value={seo_title.value}
                            onChange={value => this.onChangeSetting('seo_title', value)}
                        /> : null}
                        {seoTitleButtons}
                        {seoDescriptionButtons !== null ? <Input
                            label={`${__('SEO popis')}${allowMeta && seo_description.meta !== '' ? ` (meta: ${seo_description.meta})` : ''}`}
                            value={seo_description.value}
                            onChange={value => this.onChangeSetting('seo_description', value)}
                        /> : null}
                        {seoDescriptionButtons}
                    </>
                );
        }

        return null;
    }

    /**
     * Rendrovanie.
     *
     * @return {JSX.Element}
     */
    render() {
        const { data, tab, subtab, languages, loadingsAi, loadingsSave, loadingsRemove } = this.state;

        if (_.isEmpty(data)) {
            // Data nie su nacitane
            return <div
                className="category-content category-content-loading"
            >{this.renderLoading()}</div>;
        }

        let subtab_content = null;

        const allowChangeTab = _.isEmpty(loadingsAi) && _.isEmpty(loadingsSave) && _.isEmpty(loadingsRemove);

        if (subtab !== '') {
            switch (tab) {
                case 'translations':
                    subtab_content = (
                        <Tabs
                            value={subtab}
                            onChange={allowChangeTab ? (event, subtab) => this.onChangeSubTab(subtab) : () => {}}
                            className="category-content__panel__subtabs"
                        >
                            {_.map(languages, language => <Tab label={language.toUpperCase()} value={language} key={language} />)}
                        </Tabs>
                    );
                    break;
            }
        }

        return (
            <div className="category-content">
                <div className="category-content__panel" />
                <div className="category-content__panel">
                    <Tabs
                        value={tab}
                        onChange={allowChangeTab ? (event, tab) => this.onChangeTab(tab) : () => {}}
                        className="category-content__panel__tabs"
                    >
                        <Tab label={__('Texty')} value="translations" />
                    </Tabs>
                    {subtab_content}
                    <div className="category-content__panel__tab-content">
                        {this.renderTab()}
                    </div>
                </div>
                {this.renderSnackbar()}
            </div>
        );
    }
}

export { Category };
